/**
 * Labstep
 */

import type {
  AdditionalFilters,
  EntitySearchProps,
} from 'labstep-web/components/Entity/Search/types';
import { Filter } from 'labstep-web/constants/filters';
import { AVAILABLE_COUNTS } from 'labstep-web/core/Pagination/CountPerPage';
import type { ISearchContainerProps } from 'labstep-web/hoc/Search/types';
import type { APICallStatus } from 'labstep-web/models';
import { UserGroup } from 'labstep-web/models/user-group.model';

/**
 * Check if the deleted filter is active
 * @param searchParams
 * @returns
 */
export const isDeletedFilterActive = (
  searchParams: ISearchContainerProps['searchParams'],
) => {
  if (searchParams.is_deleted === '1') {
    return true;
  }
  if (!searchParams.filter) {
    return false;
  }

  return JSON.stringify(searchParams.filter).includes(
    `{"attribute":"deletedAt","comparison":"not_null"}`,
  );
};

export const getFilteredEntities = (
  entities: ISearchContainerProps['entities'],
  searchParams: ISearchContainerProps['searchParams'],
  filters: EntitySearchProps['filters'],
  additionalFilters?: AdditionalFilters[],
): ISearchContainerProps['entities'] => {
  let filteredEntities = entities;

  if (filters && filters.indexOf(Filter.deleted) > -1) {
    if (isDeletedFilterActive(searchParams)) {
      filteredEntities = filteredEntities.filter((entity) => {
        if (entity instanceof UserGroup) {
          return entity.group.deleted_at !== null;
        }
        return entity.deleted_at !== null;
      });
    } else {
      filteredEntities = filteredEntities.filter((entity) => {
        if (entity instanceof UserGroup) {
          return entity.group.deleted_at === null;
        }
        return entity.deleted_at === null;
      });
    }
  }

  if (filters && filters.indexOf(Filter.is_user_deleted) > -1) {
    if (searchParams.is_user_deleted === '1') {
      filteredEntities = filteredEntities.filter(
        (entity: any) => entity.user.deleted_at !== null,
      );
    } else {
      filteredEntities = filteredEntities.filter(
        (entity: any) => !entity.user.deleted_at,
      );
    }
  }

  if (filters && filters.indexOf(Filter.is_group_deleted) > -1) {
    if (searchParams.is_group_deleted === '1') {
      filteredEntities = filteredEntities.filter(
        (entity: any) => entity.group.deleted_at !== null,
      );
    } else {
      filteredEntities = filteredEntities.filter(
        (entity: any) => !entity.group.deleted_at,
      );
    }
  }

  if (additionalFilters) {
    additionalFilters.map((filter) => {
      filteredEntities = filteredEntities.filter(filter);
      return true;
    });
  }

  return filteredEntities;
};

/**
 *
 * Selector to get count per page from state
 */
export const getCountPerPage = (uiPersistentState: any): number => {
  const stateCountPerPage = uiPersistentState.count_per_page;
  // Applying a max count per page for performance. Any legacy counts larger
  // than this will fall back to the max count
  const maxCountPerPage =
    AVAILABLE_COUNTS[AVAILABLE_COUNTS.length - 1];
  if (stateCountPerPage > maxCountPerPage) {
    return maxCountPerPage;
  }
  return stateCountPerPage;
};

/**
 * Of the given statuses, returns the first one that is fetching
 * @param statuses Statuses
 * @returns
 */
export const getStatusIsFetching = (
  statuses: Record<number, APICallStatus> | undefined,
) => {
  if (!statuses) {
    return undefined;
  }
  return Object.values(statuses).find((s) => s!.isFetching);
};
